import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useToast } from '@chakra-ui/react';
import axios from 'axios';
import { useAtom } from 'jotai';
import { Action, ItemType } from '@/../../server/types/loggingTypes';
import logo from '@/assets/logo.png';
import OTPInput from '@/components/PhoneVerification/OTPInput';
import { inviteCodeAtom } from '@/store/inviteAtom';
import { setAuthInfo } from '@/store/slices/authSlice';
import { getStatsigClient } from '@/utils/statsig';

export const Route = createFileRoute('/_privateRoutes/onboarding/check-invite-code')({
  component: CheckInviteCode,
});

export default function CheckInviteCode() {
  useEffect(() => {
    getStatsigClient().logEvent('check_invite_code_page', Action.VIEW);
  }, []);

  const [inviteCode, setInviteCode] = useState('');
  const dispatch = useDispatch();
  const toast = useToast();

  const [prefilledInviteCode, setPrefilledInviteCode] = useAtom(inviteCodeAtom);

  useEffect(() => {
    if (prefilledInviteCode) {
      setInviteCode(prefilledInviteCode);
    }
  }, [prefilledInviteCode]);

  const handleInviteCodeChange = useCallback(
    (code: string) => {
      if (!inviteCode) {
        getStatsigClient().logEvent('check_invite_code_page', Action.TYPING, {
          item_name: 'invite_code',
        });
      }
      setInviteCode(code);
    },
    [inviteCode],
  );

  const handleSubmit = async () => {
    getStatsigClient().logEvent('check_invite_code_page', Action.CLICK, {
      item_name: 'submit',
      item_type: ItemType.BUTTON,
    });

    if (!inviteCode.trim()) {
      toast({
        title: 'Invite code cannot be empty.',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      const response = await axios.post('/api/invite/verifyInvite', {
        code: inviteCode.trim(),
      });

      if (response.data.success) {
        setPrefilledInviteCode(null);
        dispatch(setAuthInfo({ inviteVerified: true }));
      } else {
        throw new Error(response.data.message || 'Failed to verify invite code.');
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || error.message || 'Failed to verify invite code.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <div className="flex flex-col items-center min-h-screen bg-[#121212] px-4 pb-6 relative">
      {/* Header */}
      <div className="relative flex flex-row items-center justify-center py-3 px-4 w-full">
        <img src={logo} alt="Bond Logo" className="h-8 object-contain" />
      </div>

      <h1 className="text-white text-[34px]/[40px] tracking-tight text-center max-w-[350px] font-bold mt-8 mb-10">
        Enter your invite code
      </h1>

      <div className="w-full max-w-[334px] flex flex-col items-center">
        <OTPInput
          length={6}
          allowAlphaNumeric
          prefill={prefilledInviteCode ?? undefined}
          onChange={handleInviteCodeChange}
          onComplete={handleSubmit}
        />

        <Link
          to="/onboarding/join-waitlist"
          className="text-primary text-[16px] text-center max-w-[320px] mt-8 cursor-pointer"
        >
          Don't have a code? Join our waitlist
        </Link>
      </div>
    </div>
  );
}
